import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShotHistoryRecord } from "@/utils/historyUtils";
import { ArrowLeft, ArrowRight, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ShotStatisticsProps {
  history: ShotHistoryRecord[];
}

export const ShotStatistics: React.FC<ShotStatisticsProps> = ({ history }) => {
  const { t } = useTranslation('common');
  
  const stats = useMemo(() => {
    const shotsWithTendency = history.filter(shot => shot.tendency);
    
    if (shotsWithTendency.length === 0) {
      return null;
    }

    const leftCount = shotsWithTendency.filter(s => s.tendency === 'left').length;
    const rightCount = shotsWithTendency.filter(s => s.tendency === 'right').length;
    const total = shotsWithTendency.length;

    const averageDistance = history.reduce((acc, shot) => acc + shot.distance, 0) / history.length;

    return {
      leftCount,
      rightCount,
      total,
      leftPercent: Math.round((leftCount / total) * 100),
      rightPercent: Math.round((rightCount / total) * 100),
      averageDistance: averageDistance.toFixed(1),
      totalShots: history.length,
    };
  }, [history]);

  if (!stats || history.length === 0) {
    return null;
  }

  return (
    <Card className="w-full mt-6">
      <CardHeader>
        <CardTitle className="text-center flex items-center justify-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {t('ballDistance.statistics.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Average distance */}
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">{t('ballDistance.statistics.averageDistance')}</p>
            <p className="text-2xl font-bold">{stats.averageDistance} m</p>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalShots} {t('ballDistance.statistics.shotsRecorded')}
            </p>
          </div>

          {/* Tendency distribution */}
          {stats.total > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-center">
                {t('ballDistance.statistics.directionDistribution')} ({stats.total} {t('ballDistance.statistics.shots')})
              </p>
              
              <div className="space-y-2">
                {/* Left */}
                <div className="flex items-center gap-3">
                  <ArrowLeft className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{t('ballDistance.statistics.left')}</span>
                      <span className="text-sm font-medium">{stats.leftPercent}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${stats.leftPercent}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">
                    {stats.leftCount}
                  </span>
                </div>

                {/* Right */}
                <div className="flex items-center gap-3">
                  <ArrowRight className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{t('ballDistance.statistics.right')}</span>
                      <span className="text-sm font-medium">{stats.rightPercent}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-600 transition-all duration-300"
                        style={{ width: `${stats.rightPercent}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">
                    {stats.rightCount}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ShotStatistics;
